import { LoadingOutlined } from '@ant-design/icons';
import { Button, Empty, message, Select, Spin, Typography } from 'antd';
import { AxiosError } from 'axios';
import moment, { Moment } from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import Api from '../../../../../api/Api';
import RoseImage from '../../../../../assets/image/K13aCMEMS-Rose.png';
import DatePicker from '../../../../../lib/MomentDatePicker';
import { IBestDataset, IItemsValidation } from '../../../../../types/analytics';
import { IGetRosePlotInsituPayload } from '../../../../../types/chart';
import { MarkerItem } from '../../../../../types/home';
import { IEnrichedMetadata } from '../../../../../types/metadata';
import { UserGroupPermissions } from '../../../../../types/userGroupPermissions';
import { ValidationTabsEnum } from '../../../../../types/validation';
import {
  DownloadCheckboxKey,
  ROSE_PLOT_VALIDATION,
} from '../../../../../utils/common';
import { isAbortError } from '../../../../../utils/isAbortError';
import { DateFormat, DT0Format } from '../../../../../utils/StaticVariable';
import styles from '../../../../features/ResultPanel/Style/ValidationPanel.module.scss';
import { AnalyticsSection } from '../../../Analytics/components/Layout/AnalyticsSection';
import { ChartBox } from '../../../ResultPanel/component/ChartBox';

const { Text } = Typography;

interface IRosePlot {
  dateRange?: [moment.Moment, moment.Moment];
  validInSituPoint?: boolean;
  activePoint?: MarkerItem;
  activeTab: number;
  enrichedMetadata?: IEnrichedMetadata[];
  insituName?: string;
  listParameters: IItemsValidation[];
  activeCategoryKey: string;
  setActiveCategory: (category: string) => void;
  userGroupPermissions: UserGroupPermissions;
  datasetId: string;
  bestDataset?: IBestDataset[];
  activePanel: string;
}

const degreeIntervals = [4, 8, 12, 16, 32, 36];

const RosePlot: React.FC<IRosePlot> = ({
  activeCategoryKey,
  setActiveCategory,
  validInSituPoint,
  activePoint,
  enrichedMetadata,
  insituName,
  listParameters,
  dateRange,
  activeTab,
  userGroupPermissions,
  datasetId,
  bestDataset,
  activePanel,
}) => {
  const [variable, setVariable] = useState('');
  const [numberOfDirection, setNumberOfDirection] = useState(0);
  const [rosePlotImages, setRosePlotImages] = useState('');
  const [_, setLinks] = useState([]);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelPendingRequests = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  useEffect(() => {
    if (
      enrichedMetadata &&
      listParameters.length > 0 &&
      userGroupPermissions &&
      validInSituPoint &&
      dateRange
    ) {
      const parameter = listParameters[0].name;
      const direction = 16;
      setVariable(parameter);
      setNumberOfDirection(16);
      setStartDate(moment(dateRange[0]));
      setEndDate(moment(dateRange[1]));
      const values = {
        datasetName: datasetId,
        numberOfDirection: direction,
        parameters: parameter,
        startDate:
          startDate?.format(DT0Format) ??
          moment(dateRange[0]).format(DT0Format),
        endDate:
          endDate?.format(DT0Format) ?? moment(dateRange[1]).format(DT0Format),
      };
      if (
        activePanel === ValidationTabsEnum.INSITU_TAB &&
        activeTab === ROSE_PLOT_VALIDATION &&
        rosePlotImages === ''
      ) {
        onLoadRosePlot(values, enrichedMetadata);
      }
    }
  }, [
    activeTab,
    enrichedMetadata,
    listParameters,
    userGroupPermissions,
    activePoint?.coordinates,
    validInSituPoint,
    dateRange,
    activePanel,
  ]);

  useEffect(() => {
    const isActiveRoseTab =
      activePanel === ValidationTabsEnum.INSITU_TAB &&
      activeTab === ROSE_PLOT_VALIDATION;

    if (!isActiveRoseTab) {
      cancelPendingRequests();
      setLoading(false);
    }
  }, [activePanel, activeTab]);

  useEffect(() => {
    return () => {
      cancelPendingRequests();
    };
  }, []);

  const onLoadRosePlot = async (
    values: IGetRosePlotInsituPayload,
    enrichedMetadata: IEnrichedMetadata[]
  ) => {
    cancelPendingRequests();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setRosePlotImages('');
    setLinks([]);
    try {
      const category = bestDataset?.find(
        (bd: IBestDataset) => bd.id === values.datasetName
      );
      if (category !== undefined) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === values.datasetName &&
            task.CoordinatesString === activePoint?.coordinates.join()
        );
        if (task) {
          const res = await Api.chart.getRosePlotInsitu(
            insituName ?? '',
            task.OrderId,
            values.numberOfDirection,
            values.parameters,
            values.startDate,
            values.endDate,
            {
              signal: controller.signal,
            }
          );
          if (res?.img) {
            setRosePlotImages('data:image/png;base64,' + res.img);
          }
          if (res?.links) {
            setLinks(res.links);
          }
        }
      }
    } catch (error) {
      if (isAbortError(error as AxiosError)) {
        return;
      }
      console.log(error);
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  const onChangeStartDate = (value: any) => {
    if (value) {
      setStartDate(value);
    }
  };

  const onChangeEndDate = (value: any) => {
    if (value) {
      setEndDate(value);
    }
  };

  const onVariableChange = (value: string) => {
    setVariable(value);
  };

  const onNoOfDirectionChange = (value: number) => {
    setNumberOfDirection(value);
  };

  const onChangeParameter = (value: string) => {
    setActiveCategory(value);
  };

  const handleUpdate = () => {
    if (enrichedMetadata && datasetId && startDate && endDate) {
      const values = {
        datasetName: datasetId,
        parameters: variable,
        numberOfDirection: numberOfDirection,
        startDate: startDate?.format(DT0Format),
        endDate: endDate?.format(DT0Format),
      };
      onLoadRosePlot(values, enrichedMetadata);
    }
  };

  const showUpdateFormMessage = () => {
    message.warning('Please click Update once you finished your selection');
  };

  return (
    <AnalyticsSection
      plotInputContent={
        <div className={styles.altimeterForm}>
          <div>
            <Text>Parameters</Text>
            <Select
              value={
                activeCategoryKey
                  ? (activeCategoryKey as string)
                  : (DownloadCheckboxKey.SW as string)
              }
              onBlur={showUpdateFormMessage}
              onChange={onChangeParameter}
              className={styles.altimeterFormSelect}
              disabled={!validInSituPoint || loading}
            >
              {Object.entries(DownloadCheckboxKey).map(([key, val]) => (
                <Select.Option key={key} value={val}>
                  {val}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <Text>Variables</Text>
            <Select
              value={variable}
              onBlur={showUpdateFormMessage}
              onChange={onVariableChange}
              disabled={!validInSituPoint || loading}
              className={styles.altimeterFormSelect}
            >
              {listParameters.map((item) => {
                return (
                  <Select.Option key={item.id} value={item.name}>
                    {item.name}
                  </Select.Option>
                );
              })}
            </Select>
          </div>

          <div>
            <Text>No. of Direction</Text>
            <Select
              value={numberOfDirection}
              onBlur={showUpdateFormMessage}
              onChange={onNoOfDirectionChange}
              disabled={!validInSituPoint || loading}
              className={styles.altimeterFormSelect}
            >
              {degreeIntervals.map((interval) => {
                return (
                  <Select.Option key={interval} value={interval}>
                    {interval}
                  </Select.Option>
                );
              })}
            </Select>
          </div>

          <div>
            <Text>Start Date</Text>
            <DatePicker
              value={moment(startDate)}
              onBlur={showUpdateFormMessage}
              onChange={onChangeStartDate}
              format={DateFormat}
              disabled={!validInSituPoint || loading}
              className={styles.altimeterFormDateSelect}
            />
          </div>

          <div>
            <Text>End Date</Text>
            <DatePicker
              value={moment(endDate)}
              onBlur={showUpdateFormMessage}
              onChange={onChangeEndDate}
              format={DateFormat}
              disabled={!validInSituPoint || loading}
              className={styles.altimeterFormDateSelect}
            />
          </div>
        </div>
      }
      updateContent={
        <Button
          type='primary'
          onClick={handleUpdate}
          disabled={!validInSituPoint || loading}
          style={{ width: 100 }}
        >
          Update
        </Button>
      }
      isLoading={loading}
      chartContent={
        <ChartBox
          key={'Rose Plot'}
          title={'Rose Plot'}
          height={700}
          plot={
            <>
              {loading && (
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 50 }} spin />}
                />
              )}
              {!loading && rosePlotImages.trim() !== '' && (
                <img
                  style={{
                    width: '1200px',
                    objectFit: 'scale-down',
                    maxHeight: '65vh',
                    marginTop: '50px',
                  }}
                  src={RoseImage}
                  alt='RosePlot'
                />
              )}
              {!loading && rosePlotImages === '' && <Empty />}
            </>
          }
          dataset=''
        />
      }
    />
  );
};

export default RosePlot;
